import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LuBriefcase, LuCheck, LuChevronLeft, LuChevronRight, LuTreePalm, LuTriangleAlert } from 'react-icons/lu';
import { useTranslation } from '../../../i18n/LanguageContext';
import { normalizePatientId } from '../../../utils/patientAccess';
import { dateKey, jobState, layoutWeek, normalizeDate, pendingCompletionKeys, visitDayState, visitOverallState, visitStatusLine, visitWindow } from './calendarDates';
import { jobProjectName, leaveText } from './CalendarEntries';
import { BAR_TONES, JOB_STATE_TONES, PROJECT_STRIPES, PROJECT_SWATCHES, SCREEN_FAIL_TONE, VISIT_STATE_TONES } from './calendarTones';

// En fazla bu kadar şerit; fazlası günün altında "+N daha"
const MAX_LANES = 3;
const MAX_DOTS = 4;

/** Short weekday names, Monday first, in the UI language. */
const useWeekdays = (format) => {
  const { locale } = useTranslation();
  // 1 Ocak 2024 pazartesi
  return useMemo(
    () => Array.from({ length: 7 }, (_, index) => new Date(2024, 0, 1 + index).toLocaleDateString(locale, { weekday: format })),
    [locale, format]
  );
};

/** One visit window or job, drawn across its days in a week row. */
const SegmentBar = ({ segment, onOpenVisit, onOpenJob, projectSlotOf, projectNameOf }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const span = segment.endCol - segment.startCol + 1;
  const place = {
    gridColumn: `${segment.startCol + 1} / ${segment.endCol + 2}`,
    gridRow: segment.lane + 2,
  };
  const shortDay = (date) => date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  const edgeClass = `${segment.continuesLeft ? 'tw-ml-0 tw-rounded-l-none' : 'tw-ml-1'} ${segment.continuesRight ? 'tw-mr-0 tw-rounded-r-none' : 'tw-mr-1'}`;

  if (segment.kind === 'job') {
    const job = segment.item;
    const state = jobState(job);
    const jobProject = jobProjectName(job, projectNameOf);
    const tooltip = [
      job.title,
      jobProject ? `${label('tooltip.project')}: ${jobProject}` : null,
      `${shortDay(segment.start)} – ${shortDay(segment.end)}`,
      job.time ? `${label('tooltip.time')}: ${job.time}` : null,
      `${label('tooltip.status')}: ${label(`jobStatus.${job.status ? 'done' : 'ongoing'}`)}`,
    ].filter(Boolean).join('\n');
    // Projeye bağlı iş: başladığı parçada proje renginde düz şerit (kenarlık kesikli kalır)
    const stripe = Boolean(jobProject) && !segment.continuesLeft;
    return (
      <button
        type="button"
        title={tooltip}
        style={place}
        onClick={(event) => {
          event.stopPropagation();
          onOpenJob(job);
        }}
        className={`tw-relative tw-z-10 tw-my-px tw-flex tw-min-w-0 tw-items-center tw-gap-1 tw-overflow-hidden tw-rounded-md tw-border tw-border-dashed tw-border-slate-300 tw-bg-white/80 tw-px-1.5 tw-text-left tw-text-[11px] tw-font-semibold tw-leading-none tw-transition-[filter] hover:tw-brightness-95 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 dark:tw-border-white/15 dark:tw-bg-white/[0.05] dark:hover:tw-brightness-125 ${JOB_STATE_TONES[state].text} ${edgeClass} ${stripe ? 'tw-border-l-0 tw-pl-2' : ''}`}
      >
        {stripe && <span aria-hidden="true" className={`tw-absolute tw-inset-y-0 tw-left-0 tw-w-[3px] ${PROJECT_SWATCHES[projectSlotOf(job.projectId)]}`} />}
        {segment.continuesLeft && <LuChevronLeft aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0 tw-opacity-60" />}
        <LuBriefcase aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
        <span className={`tw-min-w-0 tw-flex-1 tw-truncate ${job.status ? 'tw-line-through tw-decoration-1' : ''}`}>{job.title}</span>
        {segment.continuesRight && <LuChevronRight aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0 tw-opacity-60" />}
      </button>
    );
  }

  const visit = segment.item;
  const state = visitOverallState(visit);
  const tone = BAR_TONES[state];
  const projectName = projectNameOf(visit.projectId);
  const patient = normalizePatientId(visit.patientId) || label('noPatient');
  const visitRange = visitWindow(visit);
  const pending = pendingCompletionKeys(visit);
  const tooltip = [
    projectName ? `${label('tooltip.project')}: ${projectName}` : null,
    `${label('tooltip.patient')}: ${patient}`,
    visit.visitNumber ? `${label('tooltip.visit')}: ${label('visitOrdinal', { n: visit.visitNumber })}` : null,
    `${label('tooltip.visitDay')}: ${segment.target.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'long' })}`,
    `${label('tooltip.window')}: ${shortDay(visitRange.start)} – ${shortDay(visitRange.end)}`,
    `${label('tooltip.status')}: ${visitStatusLine(t, visit, segment.target, { whole: true })}`,
  ].filter(Boolean).join('\n');

  return (
    <button
      type="button"
      title={tooltip}
      style={place}
      onClick={(event) => {
        event.stopPropagation();
        onOpenVisit(visit);
      }}
      className={`tw-relative tw-z-10 tw-my-px tw-flex tw-min-w-0 tw-items-center tw-overflow-hidden tw-rounded-md tw-text-left tw-text-[11px] tw-leading-none tw-transition-[filter] hover:tw-brightness-95 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 dark:hover:tw-brightness-125 ${tone.bar} ${edgeClass} ${
        segment.continuesLeft ? '' : `tw-border-l-[3px] ${PROJECT_STRIPES[projectSlotOf(visit.projectId)]}`
      }`}
    >
      {/* Vizit günü: pencerenin koyu kısmı */}
      {segment.markCol !== null && (
        <span
          aria-hidden="true"
          className={`tw-absolute tw-inset-y-0 ${tone.mark}`}
          style={{ left: `${((segment.markCol - segment.startCol) / span) * 100}%`, width: `${100 / span}%` }}
        />
      )}
      {/* Tamamlandığı gün */}
      {segment.doneCol !== null && (
        <span
          aria-hidden="true"
          className="tw-absolute tw-inset-y-0 tw-grid tw-place-items-center"
          style={{ left: `${((segment.doneCol - segment.startCol) / span) * 100}%`, width: `${100 / span}%` }}
        >
          <span className="tw-grid tw-h-4 tw-w-4 tw-place-items-center tw-rounded-full tw-bg-emerald-500 tw-text-white tw-shadow-sm">
            <LuCheck className="tw-h-2.5 tw-w-2.5" strokeWidth={3.5} />
          </span>
        </span>
      )}
      <span className="tw-relative tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1 tw-px-1.5 tw-py-[3px]">
        {segment.continuesLeft && <LuChevronLeft aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0 tw-opacity-60" />}
        {visit.visitNumber && <span className="tw-shrink-0 tw-rounded tw-bg-white/75 tw-px-1 tw-py-px tw-text-[10px] tw-font-bold tw-tabular-nums dark:tw-bg-black/25">V{visit.visitNumber}</span>}
        <span className="tw-min-w-0 tw-truncate tw-font-semibold">{patient}</span>
        {span > 2 && projectName && <span className="tw-hidden tw-min-w-0 tw-truncate tw-font-normal tw-opacity-70 2xl:tw-inline">· {projectName}</span>}
        {pending.length > 0 && <LuTriangleAlert aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0 tw-text-amber-600 dark:tw-text-amber-300" />}
      </span>
      {segment.continuesRight && <LuChevronRight aria-hidden="true" className="tw-relative tw-mr-0.5 tw-h-3 tw-w-3 tw-shrink-0 tw-opacity-60" />}
    </button>
  );
};

SegmentBar.propTypes = {
  segment: PropTypes.object.isRequired,
  onOpenVisit: PropTypes.func.isRequired,
  onOpenJob: PropTypes.func.isRequired,
  projectSlotOf: PropTypes.func.isRequired,
  projectNameOf: PropTypes.func.isRequired,
};

/**
 * The month. Wide: week rows where each visit is one bar over its window
 * (the darker part is the visit day, a tick marks the day it was done) and
 * jobs are dashed bars. Phones: a compact grid with a dot per visit on its
 * visit day and a thin line under days inside an open window. A day selects
 * it; a bar opens the visit or job right away.
 */
const MonthGrid = ({ cells, items, dayData, selectedKey, compact, showWindows, onSelectDay, onOpenVisit, onOpenJob, projectSlotOf, projectNameOf }) => {
  const { t, locale } = useTranslation();
  const weekdays = useWeekdays(compact ? 'narrow' : 'short');
  const today = normalizeDate(new Date());
  const todayKey = dateKey(today);

  const weeks = useMemo(() => {
    const rows = [];
    for (let index = 0; index < cells.length; index += 7) {
      const week = cells.slice(index, index + 7);
      rows.push({ week, ...(compact ? { segments: [], laneCount: 0 } : layoutWeek(items, week)) });
    }
    return rows;
  }, [cells, items, compact]);

  const dayLabelOf = (date) => date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });

  const numberClass = (key, date) => {
    if (key === todayKey) return 'tw-bg-teal-600 tw-text-white tw-shadow-sm tw-shadow-teal-600/30';
    if (key === selectedKey) return 'tw-bg-slate-900 tw-text-white dark:tw-bg-white dark:tw-text-slate-900';
    if (date < today) return 'tw-text-slate-400 dark:tw-text-slate-500';
    return 'tw-text-slate-700 dark:tw-text-slate-200';
  };

  const header = (
    <div role="row" className="tw-grid tw-grid-cols-7 tw-border-b tw-border-slate-200 tw-bg-slate-50/80 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
      {weekdays.map((weekday, index) => (
        <div
          key={weekday + index}
          role="columnheader"
          className={`tw-py-2 tw-text-center tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide ${index >= 5 ? 'tw-text-slate-400 dark:tw-text-slate-500' : 'tw-text-slate-500 dark:tw-text-slate-400'}`}
        >
          {weekday}
        </div>
      ))}
    </div>
  );

  if (compact) {
    return (
      <div role="grid" aria-label={t('profilePage.calendar.gridLabel')} className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/60 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
        {header}
        <div className="tw-grid tw-grid-cols-7">
          {cells.map((date, index) => {
            const border = `${index >= cells.length - 7 ? '' : 'tw-border-b'} ${index % 7 === 6 ? '' : 'tw-border-r'} tw-border-slate-200/80 dark:tw-border-white/[0.06]`;
            if (!date) return <div key={`blank-${index}`} role="gridcell" aria-hidden="true" className={`tw-h-[3.75rem] tw-bg-slate-50/50 dark:tw-bg-black/10 ${border}`} />;
            const key = dateKey(date);
            const { entries, windowVisits, leaves } = dayData(date);
            const isSelected = key === selectedKey;
            const onLeave = leaves.length > 0;
            const dots = entries.slice(0, MAX_DOTS).map((entry) => {
              if (entry.kind === 'job') return JOB_STATE_TONES[jobState(entry.item)].dot;
              return entry.item.screenFail ? SCREEN_FAIL_TONE.dot : VISIT_STATE_TONES[visitDayState(entry.item, date)].dot;
            });
            const countLabel = entries.length > 0 ? t('profilePage.calendar.entryCount', { count: entries.length }) : t('profilePage.calendar.noEntriesShort');
            return (
              <button
                key={key}
                type="button"
                role="gridcell"
                aria-selected={isSelected}
                aria-label={`${dayLabelOf(date)}, ${countLabel}${windowVisits.length ? `, ${t('profilePage.calendar.openWindows', { count: windowVisits.length })}` : ''}${onLeave ? `, ${leaveText(t, leaves)}` : ''}`}
                onClick={() => onSelectDay(date)}
                className={`tw-flex tw-h-[3.75rem] tw-flex-col tw-items-center tw-gap-1 tw-pt-1.5 tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-teal-500/60 ${border} ${
                  isSelected ? 'tw-bg-teal-500/[0.08] dark:tw-bg-teal-400/10' : onLeave ? 'tw-bg-sky-500/[0.07] dark:tw-bg-sky-400/[0.06]' : 'tw-bg-transparent hover:tw-bg-slate-900/[0.03] dark:hover:tw-bg-white/[0.03]'
                }`}
              >
                <span className={`tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-full tw-text-sm tw-font-semibold tw-tabular-nums ${numberClass(key, date)}`}>{date.getDate()}</span>
                <span className="tw-flex tw-h-1.5 tw-items-center tw-gap-[3px]">
                  {onLeave && <span className="tw-h-1.5 tw-w-3 tw-rounded-full tw-bg-sky-400" />}
                  {dots.map((dot, dotIndex) => <span key={dotIndex} className={`tw-h-1.5 tw-w-1.5 tw-rounded-full ${dot}`} />)}
                </span>
                {showWindows && windowVisits.length > 0 && <span aria-hidden="true" className="tw-h-[3px] tw-w-6 tw-rounded-full tw-bg-slate-300/80 dark:tw-bg-white/15" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div role="grid" aria-label={t('profilePage.calendar.gridLabel')} className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/60 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
      {header}
      {weeks.map(({ week, segments, laneCount }, weekIndex) => {
        const visibleLanes = laneCount <= MAX_LANES ? laneCount : MAX_LANES - 1;
        const hiddenPerCol = Array.from({ length: 7 }, (_, col) => segments.filter((segment) => segment.lane >= visibleLanes && segment.startCol <= col && segment.endCol >= col).length);
        const hasMore = hiddenPerCol.some((count) => count > 0);
        const rows = ['2.25rem', ...Array.from({ length: visibleLanes }, () => '1.5rem'), ...(hasMore ? ['1.375rem'] : []), 'minmax(0.5rem,1fr)'];
        return (
          <div
            key={weekIndex}
            role="row"
            className={`tw-grid tw-min-h-[7.25rem] tw-grid-cols-7 ${weekIndex === weeks.length - 1 ? '' : 'tw-border-b tw-border-slate-200/80 dark:tw-border-white/[0.06]'}`}
            style={{ gridTemplateRows: rows.join(' ') }}
          >
            {week.map((date, col) => {
              const edge = col === 6 ? '' : 'tw-border-r tw-border-slate-200/80 dark:tw-border-white/[0.06]';
              if (!date) {
                return <div key={`blank-${col}`} role="gridcell" aria-hidden="true" style={{ gridColumn: col + 1, gridRow: '1 / -1' }} className={`tw-bg-slate-50/50 dark:tw-bg-black/10 ${edge}`} />;
              }
              const key = dateKey(date);
              const { entries, leaves } = dayData(date);
              const isSelected = key === selectedKey;
              const onLeave = leaves.length > 0;
              const isPast = date < today;
              const countLabel = entries.length > 0 ? t('profilePage.calendar.entryCount', { count: entries.length }) : t('profilePage.calendar.noEntriesShort');
              return (
                <React.Fragment key={key}>
                  {/* Gün zemini: tıklayınca gün seçilir */}
                  <div
                    role="gridcell"
                    aria-selected={isSelected}
                    onClick={() => onSelectDay(date)}
                    style={{ gridColumn: col + 1, gridRow: '1 / -1' }}
                    className={`tw-cursor-pointer tw-transition-colors ${edge} ${
                      isSelected
                        ? 'tw-bg-teal-500/[0.07] tw-shadow-[inset_0_0_0_2px_rgba(20,184,166,0.55)] dark:tw-bg-teal-400/[0.08]'
                        : onLeave
                          ? 'tw-bg-sky-500/[0.06] hover:tw-bg-sky-500/[0.1] dark:tw-bg-sky-400/[0.05]'
                          : isPast
                            ? 'tw-bg-slate-50/60 hover:tw-bg-slate-100/70 dark:tw-bg-black/10 dark:hover:tw-bg-white/[0.03]'
                            : 'tw-bg-transparent hover:tw-bg-slate-900/[0.025] dark:hover:tw-bg-white/[0.03]'
                    }`}
                  />
                  <div style={{ gridColumn: col + 1, gridRow: 1 }} className="tw-relative tw-z-10 tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-1 tw-px-1.5 tw-pt-1">
                    <button
                      type="button"
                      onClick={() => onSelectDay(date)}
                      aria-label={`${dayLabelOf(date)}, ${countLabel}`}
                      aria-pressed={isSelected}
                      className={`tw-grid tw-h-7 tw-min-w-[1.75rem] tw-place-items-center tw-rounded-full tw-px-1 tw-text-xs tw-font-semibold tw-tabular-nums tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 ${numberClass(key, date)} ${key !== todayKey && !isSelected ? 'hover:tw-bg-slate-900/[0.06] dark:hover:tw-bg-white/10' : ''}`}
                    >
                      {date.getDate()}
                    </button>
                    {onLeave && (
                      <span title={leaveText(t, leaves)} className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1 tw-rounded-full tw-bg-sky-500/15 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-sky-800 dark:tw-text-sky-200">
                        <LuTreePalm aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
                        <span className="tw-hidden tw-truncate xl:tw-inline">{leaveText(t, leaves)}</span>
                      </span>
                    )}
                  </div>
                  {hiddenPerCol[col] > 0 && (
                    <button
                      type="button"
                      onClick={() => onSelectDay(date, { reveal: true })}
                      style={{ gridColumn: col + 1, gridRow: visibleLanes + 2 }}
                      className="tw-relative tw-z-10 tw-mx-1 tw-self-center tw-justify-self-start tw-rounded-md tw-bg-transparent tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-teal-700 hover:tw-bg-teal-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 dark:tw-text-teal-300"
                    >
                      {t('profilePage.calendar.more', { count: hiddenPerCol[col] })}
                    </button>
                  )}
                </React.Fragment>
              );
            })}
            {segments.filter((segment) => segment.lane < visibleLanes).map((segment) => (
              <SegmentBar
                key={`${segment.key}-${weekIndex}`}
                segment={segment}
                onOpenVisit={onOpenVisit}
                onOpenJob={onOpenJob}
                projectSlotOf={projectSlotOf}
                projectNameOf={projectNameOf}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

MonthGrid.propTypes = {
  cells: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  dayData: PropTypes.func.isRequired,
  selectedKey: PropTypes.string,
  compact: PropTypes.bool,
  showWindows: PropTypes.bool,
  onSelectDay: PropTypes.func.isRequired,
  onOpenVisit: PropTypes.func.isRequired,
  onOpenJob: PropTypes.func.isRequired,
  projectSlotOf: PropTypes.func.isRequired,
  projectNameOf: PropTypes.func.isRequired,
};

export default MonthGrid;
